/**
 * Die Sprache einer Fehlerseite — dieselbe Reihenfolge wie die App selbst:
 * die Adresse zuerst (auf `/{land}/{sprache}/…` ist sie die Wahrheit), dann
 * das Sprach-Cookie, dann der Browser, dann die Startsprache. Gewählt wird nur,
 * wofür Texte im Auszug liegen.
 *
 * Ohne Backend: Welches Präfix welche Sprache ist, steht in der statischen
 * Vorgabe des Routings (`utils/standardSprachen.ts`), nicht in
 * `/api/i18n/locale-config` — die Fehlerseite läuft gerade dann, wenn das
 * Backend nicht antwortet.
 */
import { DEFAULT_LANGUAGE, DEFAULT_LANGUAGES } from '../../utils/standardSprachen';

export interface SprachHinweise {
  /** Pfad der Seite: `/gb/english/pricing` → Englisch. */
  pfad?: string;
  /** Wert des Sprach-Cookies (`paperball_language`). */
  cookie?: string | null;
  /** `navigator.languages` bzw. `Accept-Language`, in Reihenfolge. */
  browser?: readonly string[];
}

const PRAEFIX = /^\/([a-z]{2})\/([a-z]+)(?=\/|$)/;

/** `/de/deutsch/portfolio-backtester` → `{ praefix: '/de/deutsch', sprache: 'de' }`, sonst `null`. */
export function praefixAusPfad(pfad: string): { praefix: string; sprache: string } | null {
  const treffer = PRAEFIX.exec(pfad);
  if (!treffer) return null;
  const sprache = Object.values(DEFAULT_LANGUAGES).find((l) => l.urlPrefix === treffer[2]);
  return sprache ? { praefix: treffer[0], sprache: sprache.code } : null;
}

export function waehleSprache(vorhanden: readonly string[], hinweise: SprachHinweise): string {
  const passt = (code: string | null | undefined): code is string =>
    code !== null && code !== undefined && vorhanden.includes(code);

  const ausPfad = praefixAusPfad(hinweise.pfad ?? '')?.sprache;
  if (passt(ausPfad)) return ausPfad;
  if (passt(hinweise.cookie)) return hinweise.cookie;
  for (const eintrag of hinweise.browser ?? []) {
    const basis = eintrag.toLowerCase().split(/[-;]/)[0].trim();
    // Norwegisch heisst im Browser oft `nb`/`nn`, in der App `no`.
    const code = basis === 'nb' || basis === 'nn' ? 'no' : basis;
    if (passt(code)) return code;
  }
  if (passt(DEFAULT_LANGUAGE)) return DEFAULT_LANGUAGE;
  throw new Error('Keine Sprache für die Fehlerseite verfügbar');
}

/** `Accept-Language: de-DE,de;q=0.9,en;q=0.8` → `['de-DE', 'de', 'en']` (nach Gewicht). */
export function acceptLanguage(kopf: string | null): string[] {
  if (!kopf) return [];
  return kopf
    .split(',')
    .map((teil, index) => {
      const [tag, ...rest] = teil.trim().split(';');
      const q = rest.find((r) => r.trim().startsWith('q='));
      return { tag: tag.trim(), gewicht: q ? Number(q.trim().slice(2)) : 1, index };
    })
    .filter((e) => e.tag !== '' && e.tag !== '*' && !Number.isNaN(e.gewicht) && e.gewicht > 0)
    .sort((a, b) => b.gewicht - a.gewicht || a.index - b.index)
    .map((e) => e.tag);
}

/** Ein Cookie aus `document.cookie` lesen. */
export function cookieWert(alle: string, name: string): string | null {
  const treffer = alle.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return treffer ? decodeURIComponent(treffer[1]) : null;
}
